import { type AccountKey, getAccountKeyForMailboxInfo, type MailboxInfo } from 'owa-client-types';
import { type M365Acquisition } from 'owa-graph-schema';
import getBizChatAppId from './getBizChatAppId';
import {
    hasBizChatLicense,
    hasM365CopilotLicense,
    isMChatFeatureEnabled,
} from 'owa-copilot-settings-utils';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { isOcpsPolicyEnabled, OcpsPolicy } from 'owa-ocps-policy-store';
import { BizChatSidepaneEnablementCodes } from 'owa-copilot-settings-store/lib/utils/enablementCodes/BizChatSidepaneEnablementCodes';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isConsumer } from 'owa-session-store';
import { logCoreUsage } from 'owa-analytics';

const loggedMailboxes = new Set<AccountKey>();

export function filterBizChatAcquisitionEdges(
    m365Acquisitions: M365Acquisition[],
    mailboxInfo?: MailboxInfo,
    shouldIgnoreUserPreferences?: boolean
): M365Acquisition[] {
    const mboxInfo = mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    const bizChatAppId = getBizChatAppId(mboxInfo);
    const isMChatEnabled = isMChatFeatureEnabled(
        mboxInfo,
        undefined /* skipLanguageCheck */,
        shouldIgnoreUserPreferences
    );
    // We filter MOS3 response only based on OCS response
    return m365Acquisitions.filter(
        edge =>
            edge.appId !== bizChatAppId ||
            (isMChatEnabled && !isBizchatSidepaneDisabledByPolicy(mboxInfo))
    );
}

export function isBizchatSidepaneDisabledByPolicy(mailboxInfo: MailboxInfo): boolean {
    // We only check for tenant policies if user is Non-AI SKU and Not consumer
    const isConsumerUser = isConsumer(undefined, mailboxInfo);
    if (
        !isFeatureEnabled('mon-copilot-bizchat-ocps-check-killswitch') &&
        !isUserAiSku(mailboxInfo) &&
        !isConsumerUser
    ) {
        const isCopilotBlockAccessToOpenFilesEnabled = isOcpsPolicyEnabled(
            OcpsPolicy.CopilotBlockAccessToOpenFiles,
            /*enabledIfUndefined*/ false // If policy is undefined, we should not enable block access to open files
        );

        const isCopilotSidepaneEnabled = isOcpsPolicyEnabled(
            OcpsPolicy.CopilotChatSidepane,
            /*enabledIfUndefined*/ true // If policy is undefined, we should enable chat sidepane
        );

        const isDisabled = isCopilotBlockAccessToOpenFilesEnabled || !isCopilotSidepaneEnabled;
        const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);

        if (isDisabled && !loggedMailboxes.has(accountKey)) {
            loggedMailboxes.add(accountKey);

            const enablementDiagnostics = isCopilotBlockAccessToOpenFilesEnabled
                ? BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_DISABLED_POLICY_BLOCK_ACCESS_TO_FILES
                : BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_DISABLED_OCPS_POLICY;

            logCoreUsage('BizchatSidepaneEnablementDiagnostics', mailboxInfo, {
                enablementDiagnostics,
                diagnosticsSource: 'filterBizChatAcquisition',
            });
        }

        return isDisabled;
    }
    return false;
}

function isUserAiSku(mailboxInfo: MailboxInfo): boolean {
    return hasBizChatLicense(mailboxInfo) || hasM365CopilotLicense(mailboxInfo);
}
